import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  FiX, 
  FiUser, 
  FiMail, 
  FiPhone, 
  FiMapPin, 
  FiSave,
  FiLock,
  FiUnlock,
  FiEye,
  FiEyeOff,
  FiAlertCircle,
  FiCheckCircle,
  FiShield,
  FiRefreshCw
} from "react-icons/fi";
import { 
  FaCity, 
  FaBuilding,
  FaUserCircle,
  FaEnvelope,
  FaPhoneAlt,
  FaKey,
  FaShieldAlt
} from "react-icons/fa";
import { toast } from "react-toastify";

const EditCustomerModal = ({ customer, isOpen, onClose, onUpdate }) => {
  const [formData, setFormData] = useState({
    customerName: customer?.customerName || "",
    customerEmail: customer?.customerEmail || "",
    customerPhone: customer?.customerPhone || "",
    city: customer?.city || "",
    state: customer?.state || ""
  });

  const [passwordData, setPasswordData] = useState({
    newPassword: "",
    confirmPassword: ""
  });

  const [showPasswordSection, setShowPasswordSection] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);

  // Reset form data when customer changes
  useEffect(() => {
    if (customer) {
      setFormData({
        customerName: customer.customerName || "",
        customerEmail: customer.customerEmail || "",
        customerPhone: customer.customerPhone || "",
        city: customer.city || "",
        state: customer.state || ""
      });
    }
  }, [customer]);

  // Reset password fields when hiding section
  useEffect(() => {
    if (!showPasswordSection) {
      setPasswordData({
        newPassword: "",
        confirmPassword: ""
      });
      setShowNewPassword(false);
      setShowConfirmPassword(false);
      setPasswordStrength(0);
    }
  }, [showPasswordSection]);

  const checkPasswordStrength = (password) => {
    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) strength++;
    setPasswordStrength(strength);
    return strength;
  };

  const getStrengthColor = () => {
    if (passwordStrength <= 2) return 'bg-red-500';
    if (passwordStrength <= 3) return 'bg-yellow-500';
    if (passwordStrength <= 4) return 'bg-blue-500';
    return 'bg-emerald-500';
  };

  const getStrengthText = () => {
    if (passwordStrength <= 2) return 'Weak';
    if (passwordStrength <= 3) return 'Fair';
    if (passwordStrength <= 4) return 'Good';
    return 'Strong';
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData({
      ...passwordData,
      [name]: value
    });
    if (name === 'newPassword') {
      checkPasswordStrength(value);
    }
  };

  const validatePassword = () => {
    if (showPasswordSection) {
      if (passwordData.newPassword !== passwordData.confirmPassword) {
        toast.error("Passwords do not match");
        return false;
      }
      if (passwordData.newPassword.length < 8) {
        toast.error("Password must be at least 8 characters long");
        return false;
      }
      if (passwordStrength < 3) {
        toast.warning("Password is weak. Please use a stronger password");
        return false;
      }
      return true;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validatePassword()) return;

    setIsSubmitting(true);
    try {
      const updateData = {
        ...formData,
        ...(showPasswordSection && {
          password: passwordData.newPassword
        })
      };
      
      await onUpdate(updateData);
      onClose();
    } catch (error) {
      toast.error("Failed to update customer");
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClasses = "w-full px-4 py-3 rounded-xl border transition-all duration-200 outline-none bg-gray-50/50 focus:bg-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200";
  const labelClasses = "block text-sm font-semibold text-gray-700 mb-2";
  const errorClasses = "text-xs text-red-500 mt-1 flex items-center gap-1";

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm"
          />

          {/* Modal */}
          <div className="flex min-h-screen items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl overflow-hidden"
            >
              {/* Header with Gradient */}
              <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-white/20 rounded-xl">
                      <FaUserCircle className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-white">Edit Customer</h2>
                      <p className="text-indigo-100 text-sm mt-0.5">Update customer information</p>
                    </div>
                  </div>
                  <button
                    onClick={onClose}
                    className="text-white/80 hover:text-white hover:bg-white/20 rounded-xl p-2 transition-all"
                  >
                    <FiX className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Form Content */}
              <form onSubmit={handleSubmit} className="p-6 max-h-[70vh] overflow-y-auto custom-scrollbar">
                <div className="space-y-6">
                  {/* Basic Information Section */}
                  <div>
                    <div className="flex items-center gap-2 mb-4 pb-2 border-b border-gray-200">
                      <div className="w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center">
                        <FiUser className="w-4 h-4 text-indigo-600" />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-800">Basic Information</h3>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div>
                        <label className={labelClasses}>
                          Full Name <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <FiUser className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                          <input
                            type="text"
                            name="customerName"
                            value={formData.customerName}
                            onChange={handleChange}
                            className={`${inputClasses} pl-10`}
                            required
                            placeholder="Enter customer name"
                          />
                        </div>
                      </div>

                      <div>
                        <label className={labelClasses}>
                          Email Address <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <FaEnvelope className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                          <input
                            type="email"
                            name="customerEmail"
                            value={formData.customerEmail}
                            onChange={handleChange}
                            className={`${inputClasses} pl-10`}
                            required
                            placeholder="Enter email address"
                          />
                        </div>
                      </div>

                      <div>
                        <label className={labelClasses}>
                          Phone Number <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <FaPhoneAlt className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                          <input
                            type="tel"
                            name="customerPhone"
                            value={formData.customerPhone}
                            onChange={handleChange}
                            className={`${inputClasses} pl-10`}
                            required
                            placeholder="Enter phone number"
                          />
                        </div>
                      </div>

                      <div>
                        <label className={labelClasses}>
                          City <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <FaCity className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                          <input
                            type="text"
                            name="city"
                            value={formData.city}
                            onChange={handleChange}
                            className={`${inputClasses} pl-10`}
                            required
                            placeholder="Enter city"
                          />
                        </div>
                      </div>

                      <div className="md:col-span-2">
                        <label className={labelClasses}>
                          State <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <FaBuilding className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                          <input
                            type="text"
                            name="state"
                            value={formData.state}
                            onChange={handleChange}
                            className={`${inputClasses} pl-10`}
                            required
                            placeholder="Enter state"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Password Update Section */}
                  <div className="pt-4">
                    <div className="flex items-center gap-2 mb-4 pb-2 border-b border-gray-200">
                      <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
                        <FaKey className="w-4 h-4 text-amber-600" />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-800">Security Settings</h3>
                    </div>

                    <div className="bg-amber-50 rounded-xl p-4 border border-amber-200 mb-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={showPasswordSection}
                          onChange={(e) => setShowPasswordSection(e.target.checked)}
                          className="w-4 h-4 text-amber-600 rounded focus:ring-amber-500"
                        />
                        <span className="text-sm font-medium text-amber-800">Update Password</span>
                      </label>
                      <p className="text-xs text-amber-700 mt-1 ml-6">Check this box to change the customer's password</p>
                    </div>

                    {showPasswordSection && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        className="space-y-5"
                      >
                        <div>
                          <label className={labelClasses}>
                            New Password <span className="text-red-500">*</span>
                          </label>
                          <div className="relative">
                            <FaKey className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                            <input
                              type={showNewPassword ? "text" : "password"}
                              name="newPassword"
                              value={passwordData.newPassword}
                              onChange={handlePasswordChange}
                              className={`${inputClasses} pl-10 pr-10`}
                              minLength="8"
                              required
                              placeholder="Enter new password"
                            />
                            <button
                              type="button"
                              onClick={() => setShowNewPassword(!showNewPassword)}
                              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            >
                              {showNewPassword ? <FiEyeOff className="w-4 h-4" /> : <FiEye className="w-4 h-4" />}
                            </button>
                          </div>
                          
                          {/* Password Strength Indicator */}
                          {passwordData.newPassword && (
                            <div className="mt-2 space-y-1">
                              <div className="flex items-center gap-2">
                                <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                                  <div 
                                    className={`h-full ${getStrengthColor()} transition-all duration-300`}
                                    style={{ width: `${(passwordStrength / 5) * 100}%` }}
                                  />
                                </div>
                                <span className={`text-xs font-semibold ${getStrengthColor().replace('bg-', 'text-')}`}>
                                  {getStrengthText()}
                                </span>
                              </div>
                              <p className="text-xs text-gray-500">
                                Password must be at least 8 characters with uppercase, lowercase, number, and special character
                              </p>
                            </div>
                          )}
                        </div>

                        <div>
                          <label className={labelClasses}>
                            Confirm Password <span className="text-red-500">*</span>
                          </label>
                          <div className="relative">
                            <FiShield className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                            <input
                              type={showConfirmPassword ? "text" : "password"}
                              name="confirmPassword"
                              value={passwordData.confirmPassword}
                              onChange={handlePasswordChange}
                              className={`${inputClasses} pl-10 pr-10`}
                              minLength="8"
                              required
                              placeholder="Confirm new password"
                            />
                            <button
                              type="button"
                              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            >
                              {showConfirmPassword ? <FiEyeOff className="w-4 h-4" /> : <FiEye className="w-4 h-4" />}
                            </button>
                          </div>
                          {passwordData.confirmPassword && passwordData.newPassword !== passwordData.confirmPassword && (
                            <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                              <FiAlertCircle className="w-3 h-3" />
                              Passwords do not match
                            </p>
                          )}
                          {passwordData.confirmPassword && passwordData.newPassword === passwordData.confirmPassword && passwordData.newPassword && (
                            <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1">
                              <FiCheckCircle className="w-3 h-3" />
                              Passwords match
                            </p>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end gap-3 mt-8 pt-4 border-t border-gray-100">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-5 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-xl transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex items-center gap-2 px-6 py-2.5 text-sm font-medium bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <>
                        <FiRefreshCw className="w-4 h-4 animate-spin" />
                        Updating...
                      </>
                    ) : (
                      <>
                        <FiSave className="w-4 h-4" />
                        Update Customer
                      </>
                    )}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default EditCustomerModal;

// Custom scrollbar styles
const styles = `
  .custom-scrollbar::-webkit-scrollbar {
    width: 6px;
  }
  .custom-scrollbar::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }
  .custom-scrollbar::-webkit-scrollbar-thumb {
    background: #c7d2fe;
    border-radius: 3px;
  }
  .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background: #818cf8;
  }
`;